import { useMemo } from 'react';
import { CHART_STYLE, truncateLabel, formatDataValue, analyzeDecimals } from './chartStyles';
import type { ChartConfig } from '../../../types';

interface CandlestickViewProps {
  data: Record<string, unknown>[];
  config: ChartConfig;
}

interface Candle {
  x: string;
  open: number;
  high: number;
  low: number;
  close: number;
  bullish: boolean;
}

/**
 * Candlestick chart (Rule 1a: OHLC + Date).
 * Custom SVG rendering since Recharts doesn't have a native candlestick.
 */
export function CandlestickView({ data, config }: CandlestickViewProps) {
  const xKey = config.xAxis ?? '';
  const yKeys = Array.isArray(config.yAxis) ? config.yAxis : [];

  const candles: Candle[] = useMemo(() => {
    const [openKey, highKey, lowKey, closeKey] = yKeys;
    if (!openKey || !highKey || !lowKey || !closeKey) return [];

    return data.map((row) => ({
      x: String(row[xKey] ?? ''),
      open: Number(row[openKey]) || 0,
      high: Number(row[highKey]) || 0,
      low: Number(row[lowKey]) || 0,
      close: Number(row[closeKey]) || 0,
      bullish: (Number(row[closeKey]) || 0) >= (Number(row[openKey]) || 0),
    }));
  }, [data, xKey, yKeys]);

  const { hasDecimals, allSmall } = analyzeDecimals(data, yKeys[3] ?? '');

  if (candles.length === 0) {
    return <p className="text-xs text-gray-500">Cannot render candlestick: missing OHLC columns.</p>;
  }

  // SVG dimensions
  const W = 600;
  const H = 300;
  const pad = { top: 20, bottom: 80, left: 60, right: 20 };
  const plotW = W - pad.left - pad.right;
  const plotH = H - pad.top - pad.bottom;

  const allValues = candles.flatMap((c) => [c.high, c.low]);
  const minVal = Math.min(...allValues);
  const maxVal = Math.max(...allValues);
  const range = maxVal - minVal || 1;

  const candleW = Math.max(4, (plotW / candles.length) * 0.6);
  const gap = plotW / candles.length;
  const y = (val: number) => pad.top + (1 - (val - minVal) / range) * plotH;

  return (
    <div className="overflow-x-auto">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ minWidth: 400, height: 300 }}>
        {/* Y-axis grid */}
        {[0, 0.25, 0.5, 0.75, 1].map((pct) => {
          const val = minVal + pct * range;
          return (
            <g key={pct}>
              <line x1={pad.left} y1={y(val)} x2={W - pad.right} y2={y(val)} stroke="#E5E7EB" strokeDasharray="3 3" />
              <text x={pad.left - 6} y={y(val) + 3} textAnchor="end" fontSize={CHART_STYLE.fontSize.tick} fill={CHART_STYLE.yAxis.tickFill}>
                {formatDataValue(val, hasDecimals, allSmall)}
              </text>
            </g>
          );
        })}

        {/* Candles */}
        {candles.map((c, i) => {
          const cx = pad.left + i * gap + gap / 2;
          const bodyTop = y(Math.max(c.open, c.close));
          const bodyBot = y(Math.min(c.open, c.close));
          const bodyH = Math.max(1, bodyBot - bodyTop);
          const color = c.bullish ? '#059669' : '#DC2626';

          return (
            <g key={i}>
              {/* Wick */}
              <line x1={cx} y1={y(c.high)} x2={cx} y2={y(c.low)} stroke={color} strokeWidth={1} />
              {/* Body */}
              <rect
                x={cx - candleW / 2}
                y={bodyTop}
                width={candleW}
                height={bodyH}
                fill={c.bullish ? color : color}
                fillOpacity={c.bullish ? 0.3 : 0.8}
                stroke={color}
                strokeWidth={1}
                rx={1}
              />
            </g>
          );
        })}

        {/* X-axis labels */}
        {candles.map((c, i) => {
          // Show every Nth label to prevent overlap
          const step = Math.max(1, Math.floor(candles.length / 10));
          if (i % step !== 0) return null;
          const cx = pad.left + i * gap + gap / 2;
          return (
            <text
              key={i}
              x={cx}
              y={H - pad.bottom + 14}
              textAnchor="end"
              fontSize={CHART_STYLE.fontSize.tick}
              fill={CHART_STYLE.xAxis.tickFill}
              transform={`rotate(-45, ${cx}, ${H - pad.bottom + 14})`}
            >
              {truncateLabel(c.x)}
            </text>
          );
        })}
      </svg>
    </div>
  );
}
